import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Col, InputNumber, Row, Tag, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

// OVO-268 follow-up (Diego, Aug 2026) — client-side profitability SIMULATOR on
// the partner Stats tab. No rate exists in the DB yet (that is the
// « Rentabilité partenaires » design): the rates typed here live in
// localStorage only and every figure is derived from the stats payload the
// page already has (service time + prise de service + completed orders).
// ADMIN-ONLY — driver cost and margins must never reach a partner viewer
// (OVO-002); the parent renders this only when !viewOnly.

const STORAGE_KEY = 'ovo-profitability-sim-v1';

const loadSaved = () => {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
  } catch (e) {
    return {};
  }
};

const chf = (v) => (v == null || !isFinite(v) ? '—' : `${Math.round(v).toLocaleString('fr-CH')} CHF`);

const fmtHours = (h) => {
  const mins = Math.round((h || 0) * 60);
  const hh = Math.floor(mins / 60);
  const mm = mins % 60;
  return hh > 0 ? `${hh}h ${String(mm).padStart(2, '0')}m` : `${mm}m`;
};

const ProfitabilitySimulator = ({ stats }) => {
  const { t } = useTranslation('stats');
  const saved = useMemo(loadSaved, []);
  const [hourlyRate, setHourlyRate] = useState(saved.hourlyRate ?? 90);
  const [perOrderRate, setPerOrderRate] = useState(saved.perOrderRate ?? 8);
  const [driverCost, setDriverCost] = useState(saved.driverCost ?? 35);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ hourlyRate, perOrderRate, driverCost }));
    } catch (e) {
      // private mode — the simulation still works, just not persisted
    }
  }, [hourlyRate, perOrderRate, driverCost]);

  const csH = (stats?.routes?.totalClientServiceMins ?? 0) / 60;
  const priseH = (stats?.routes?.totalPriseDeServiceMins ?? 0) / 60;
  const attribH = csH + priseH;
  const completed = stats?.delivery?.completed ?? 0;

  const cost = driverCost > 0 && attribH > 0 ? attribH * driverCost : null;
  const revenueH = csH > 0 ? csH * (hourlyRate || 0) : null;
  const revenueO = completed > 0 ? completed * (perOrderRate || 0) : null;
  const marginH = revenueH != null && cost != null ? revenueH - cost : null;
  const marginO = revenueO != null && cost != null ? revenueO - cost : null;
  const breakevenH = cost != null && csH > 0 ? cost / csH : null;
  const breakevenO = cost != null && completed > 0 ? cost / completed : null;

  const marginStyle = (m) => ({
    fontWeight: 700,
    color: m == null ? '#9e9e9e' : m >= 0 ? '#2e7d32' : '#c62828',
  });
  const pct = (m, rev) => (m == null || !rev ? null : ` (${Math.round((m / rev) * 100)}%)`);

  const scenario = (titleKey, revenue, margin, breakeven, breakevenSuffix) => (
    <Card size="small" className="stat-card" style={{ height: '100%' }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>{t(titleKey)}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>{t('profitSim.revenue')}</span>
        <span>{chf(revenue)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>{t('profitSim.cost')}</span>
        <span>{chf(cost)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        <span>{t('profitSim.margin')}</span>
        <span style={marginStyle(margin)}>
          {chf(margin)}
          {pct(margin, revenue)}
        </span>
      </div>
      <div style={{ marginTop: 8, fontSize: 12, color: '#9e9e9e' }}>
        {t('profitSim.breakeven', {
          value: breakeven == null ? '—' : `${breakeven.toFixed(2)} ${breakevenSuffix}`,
        })}
      </div>
    </Card>
  );

  const input = (labelKey, value, onChange) => (
    <div>
      <div style={{ fontSize: 12, color: '#616161', marginBottom: 4 }}>{t(labelKey)}</div>
      <InputNumber
        min={0}
        step={1}
        value={value}
        onChange={(v) => onChange(v ?? 0)}
        style={{ width: '100%' }}
        parser={(v) =>
          v == null
            ? ''
            : String(v)
                .replace(/,/g, '.')
                .replace(/[^\d.\-]/g, '')
        }
        formatter={(v) => (v == null || v === '' ? '' : String(v))}
      />
    </div>
  );

  return (
    <Card
      className="chart-card"
      style={{ marginBottom: 16 }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span role="img" aria-label="money">
            💰
          </span>
          <span>{t('profitSim.title')}</span>
          <Tag color="orange">{t('profitSim.simBadge')}</Tag>
        </div>
      }
    >
      <div style={{ color: '#616161', fontSize: 13, marginBottom: 16 }}>{t('profitSim.disclaimer')}</div>
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={6}>
          {input('profitSim.hourlyRate', hourlyRate, setHourlyRate)}
        </Col>
        <Col xs={12} sm={6}>
          {input('profitSim.perOrderRate', perOrderRate, setPerOrderRate)}
        </Col>
        <Col xs={12} sm={6}>
          {input('profitSim.driverCost', driverCost, setDriverCost)}
        </Col>
        <Col xs={12} sm={6}>
          <div style={{ fontSize: 12, color: '#616161', marginBottom: 4 }}>
            {t('profitSim.attributedHours')}
            <Tooltip title={t('profitSim.attributedHoursHint')} overlayStyle={{ maxWidth: 360 }}>
              <InfoCircleOutlined style={{ marginLeft: 6, color: '#9e9e9e' }} />
            </Tooltip>
          </div>
          <div style={{ fontSize: 20, fontWeight: 600, paddingTop: 2 }}>
            {fmtHours(attribH)}
            <span style={{ fontSize: 12, color: '#9e9e9e', marginLeft: 8 }}>
              = {fmtHours(csH)} + {fmtHours(priseH)}
            </span>
          </div>
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={12}>
          {scenario('profitSim.scenarioHourly', revenueH, marginH, breakevenH, 'CHF/h')}
        </Col>
        <Col xs={24} md={12}>
          {scenario('profitSim.scenarioPerOrder', revenueO, marginO, breakevenO, 'CHF')}
        </Col>
      </Row>
      <div style={{ marginTop: 12, fontSize: 12, color: '#9e9e9e' }}>
        {t('profitSim.footnote', { orders: completed })}
      </div>
    </Card>
  );
};

export default ProfitabilitySimulator;
